<template>
    <div ref="componentRef" class="min-h-screen bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5">
            <div v-if="loading" class="flex justify-center items-center min-h-[400px]">
                <Loader size="large" />
            </div>

            <div v-else-if="author" class="mb-2">
                <div class="grid grid-cols-1 md:grid-cols-12 gap-1 mb-1">
                    <div class="md:col-span-4">
                        <div v-if="author.image_url" :style="getAuthorImageContainerStyleLocal()"
                            class="bg-creme-200 overflow-hidden shadow-lg mb-1 w-full">
                            <img :src="author.image_url" :alt="author.name" :style="getAuthorImageStyleLocal()"
                                class="w-full h-full" />
                        </div>
                        <div v-else :style="getAuthorImageContainerStyleLocal()"
                            class="bg-creme-200 flex items-center justify-center mb-1 w-full">
                            <svg class="w-24 h-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                    </div>

                    <div class="md:col-span-8">
                        <h1 class="font-body text-[3rem] md:text-[3rem] font-bold text-primary mb-0.5">
                            {{ author.name }}
                        </h1>

                        <p v-if="author.location" class="font-body text-[1.125rem] text-gray-600 leading-[1.75] mb-0.5">
                            {{ author.location }}
                        </p>

                        <div v-if="author.name || author.location" class="border-t border-gray-300 my-1"></div>

                        <div v-if="author.description" class="prose max-w-none mb-1">
                            <div class="font-body text-[1.125rem] text-gray-700 leading-[1.75]"
                                v-html="author.description"></div>
                        </div>

                        <div v-if="author.website" class="mb-0.5">
                            <a :href="author.website" target="_blank" rel="noopener noreferrer"
                                class="text-accent hover:text-green-600 font-header font-semibold underline">
                                Visit Author Website
                            </a>
                        </div>

                        <div v-if="author.socialMedia && Object.keys(author.socialMedia).length > 0" class="flex gap-1">
                            <a v-if="author.socialMedia.twitter" :href="author.socialMedia.twitter" target="_blank"
                                rel="noopener noreferrer" class="text-primary hover:text-accent transition-colors">
                                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                    <path
                                        d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
                                </svg>
                            </a>
                            <a v-if="author.socialMedia.instagram" :href="author.socialMedia.instagram" target="_blank"
                                rel="noopener noreferrer" class="text-primary hover:text-accent transition-colors">
                                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                    <path
                                        d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                </svg>
                            </a>
                            <a v-if="author.socialMedia.facebook" :href="author.socialMedia.facebook" target="_blank"
                                rel="noopener noreferrer" class="text-primary hover:text-accent transition-colors">
                                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                    <path
                                        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>

                <div v-if="authorBooks && authorBooks.length > 0" class="mt-2">
                    <h2 class="font-header text-[1.5rem] md:text-[1.5rem] font-bold text-primary mb-1">
                        Books by <span>{{ author.name }}</span>
                    </h2>
                    <div class="flex flex-wrap gap-1 justify-start">
                        <BookCard v-for="book in authorBooks" :key="book.id" :book="book" />
                    </div>
                </div>
            </div>

            <div v-else class="text-center py-1">
                <h2 class="font-header text-[1.5rem] font-bold text-primary mb-0.5">Author Not Found</h2>
                <p class="font-body text-[1.125rem] text-gray-600 leading-[1.75] mb-1">The author you are looking for
                    does not exist.</p>
                <a href="/books" class="text-accent hover:text-green-600 font-header font-semibold underline">
                    Browse All Books
                </a>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import BookCard from './BookCard.vue'
import Loader from './Loader.vue'
import { createAxiosInstance } from '../utils/axios.js'
import { translationService } from '../services/translation'
import { useLoadingState } from '../composables/useLoadingState'
import { useAuthorImageStyle } from '../composables/useAuthorImageStyle'
import { useDataTranslation } from '../composables/useDataTranslation'

const { getAuthorImageStyle, getAuthorImageContainerStyle } = useAuthorImageStyle()

const api = createAxiosInstance()
api.interceptors.request.use(config => {
    const language = translationService.getLanguage()
    config.headers['Accept-Language'] = language
    config.headers['X-Language'] = language
    return config
})

const componentRef = ref(null)
const author = ref(null)
const authorBooks = ref([])
const { loading, setLoading } = useLoadingState(true)

const { translatePage, handleLanguageChange: handleTranslation } = useDataTranslation(componentRef, loading, '.min-h-screen.bg-white', setLoading)

const handleLanguageChange = async () => {
    const authorName = getAuthorName()
    if (authorName && authorName !== 'author') {
        await loadAuthor(authorName)
    }
    await handleTranslation()
}

onMounted(() => {
    window.addEventListener('language-changed', handleLanguageChange)
})

onUnmounted(() => {
    window.removeEventListener('language-changed', handleLanguageChange)
})

const getAuthorName = () => {
    const pathParts = window.location.pathname.split('/')
    return decodeURIComponent(pathParts[pathParts.length - 1])
}

const loadAuthor = async (authorName) => {
    setLoading(true)
    try {
        const encodedName = encodeURIComponent(authorName)
        const response = await api.get(`/api/authors/by-name/${encodedName}`)

        const data = response.data
        if (!data || !data.id || !data.name) {
            author.value = null
            authorBooks.value = []
            return
        }

        const currentLang = translationService.getLanguage()
        const needsTranslation = currentLang !== 'en'

        let translatedDescription = data.description
        let translatedLocation = data.location

        if (needsTranslation) {
            const textsToTranslate = []
            const translationMap = []

            if (data.description) {
                textsToTranslate.push(data.description)
                translationMap.push({ type: 'description' })
            }

            if (data.location) {
                textsToTranslate.push(data.location)
                translationMap.push({ type: 'location' })
            }

            if (textsToTranslate.length > 0) {
                try {
                    const translatedTexts = await translationService.translateBulk(textsToTranslate, currentLang)

                    translationMap.forEach(({ type }, index) => {
                        const translated = translatedTexts[index]
                        if (type === 'description') {
                            translatedDescription = translated
                        } else if (type === 'location') {
                            translatedLocation = translated
                        }
                    })
                } catch (error) {
                    console.warn('Bulk translation failed, using original texts:', error)
                }
            }
        }

        author.value = {
            id: data.id,
            name: data.name,
            image_url: data.image_url,
            description: translatedDescription,
            location: translatedLocation,
            imageCropDimensions: data.image_crop_dimensions || null
        }

        authorBooks.value = (data.books || []).map(book => ({
            id: book.id,
            title: book.title,
            author: book.author,
            price: book.price,
            coverImage: book.cover_image,
            imageCropDimensions: book.image_crop_dimensions || null
        }))
    } catch (error) {
        if (error.response && error.response.status === 400) {
            author.value = null
            authorBooks.value = []
        } else {
            console.error('Error loading author:', error)
            author.value = null
            authorBooks.value = []
        }
    } finally {
        setLoading(false)
    }
}

const getAuthorImageStyleLocal = () => {
    return getAuthorImageStyle(author.value)
}

const getAuthorImageContainerStyleLocal = () => {
    return getAuthorImageContainerStyle(author.value, 400)
}

onMounted(async () => {
    const authorName = getAuthorName()
    if (authorName && authorName !== 'author') {
        await loadAuthor(authorName)
        await translatePage()
    } else {
        setLoading(false)
    }

    window.addEventListener('popstate', async () => {
        const newAuthorName = getAuthorName()
        if (newAuthorName && newAuthorName !== 'author') {
            await loadAuthor(newAuthorName)
            await translatePage()
        }
    })
})
</script>

<style scoped>
:deep(a) {
    color: #2563eb;
    text-decoration: underline;
    transition: color 0.2s;
}

:deep(a):hover {
    color: #1e40af;
}
</style>
